import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import SectionHeading from "../components/SectionHeading";
import { ArrowLeft, Link2, Facebook, Twitter, Linkedin, Mail, Check } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const SITE_URL = "https://www.whenpigsflyfamilybbq.com";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${backendUrl}${u}` : u);
// For OG image tags, absolutize backend-hosted uploads too so social crawlers can fetch them.
const absoluteImg = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${backendUrl}${u}`;
  return `${SITE_URL}${u}`;
};

function fmtDate(d) {
  try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d; }
}

function upsertJsonLd(id, obj) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function removeJsonLd(id) {
  const el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (el) el.remove();
}

function SharePost({ post, url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — try again"); }
  };
  const t = encodeURIComponent(post.title);
  const u = encodeURIComponent(url);
  const excerpt = encodeURIComponent(post.excerpt || "");
  const share = [
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${u}`, Icon: Facebook, id: "facebook" },
    { name: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${u}&text=${t}`, Icon: Twitter, id: "twitter" },
    { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`, Icon: Linkedin, id: "linkedin" },
    { name: "Email", href: `mailto:?subject=${t}&body=${excerpt}%0A%0A${u}`, Icon: Mail, id: "email" },
  ];
  return (
    <div className="mt-12 border-y border-ink py-6" data-testid="blog-share-row">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">Share this post</span>
        <button
          type="button"
          onClick={copy}
          data-testid="blog-share-copy"
          className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone transition-colors hover:bg-transparent hover:text-ink"
          aria-label="Copy link"
        >
          {copied ? <Check size={14} /> : <Link2 size={14} />}
          {copied ? "Copied" : "Copy Link"}
        </button>
        {share.map((s) => (
          <a
            key={s.id}
            href={s.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`blog-share-${s.id}`}
            className="inline-flex items-center gap-2 border border-ink px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ink hover:text-bone"
            aria-label={`Share on ${s.name}`}
          >
            <s.Icon size={14} /> {s.name}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Blog() {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);

  const postImage = post ? absoluteImg(post.image_url) : null;
  usePageMeta({
    title: post ? post.title : "Blog · Smoke Signals",
    description: post
      ? (post.excerpt || "Read this post from the pit crew at When Pigs Fly Family BBQ.")
      : "Recipes, competition recaps, catering stories and pit-tested tips from When Pigs Fly Family BBQ in Spring Hill, FL.",
    path: slug ? `/blog/${slug}` : "/blog",
    image: postImage,
  });

  // JSON-LD Article schema for the detail page — helps Google rich results.
  useEffect(() => {
    // Ensure og:type is correct for every route: 'article' on a post detail, 'website' on the list.
    let ogType = document.head.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    if (slug && post) {
      ogType.setAttribute("content", "article");
      upsertJsonLd("blog-article", {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt || "",
        image: postImage ? [postImage] : undefined,
        datePublished: post.date,
        author: { "@type": "Organization", name: "When Pigs Fly Family BBQ" },
        publisher: {
          "@type": "Organization",
          name: "When Pigs Fly Family BBQ",
          logo: { "@type": "ImageObject", url: `${SITE_URL}/pig/flying-pig.png` },
        },
        mainEntityOfPage: `${SITE_URL}/blog/${slug}`,
        articleSection: post.tag,
      });
    } else {
      ogType.setAttribute("content", "website");
      removeJsonLd("blog-article");
    }
    return () => { if (!slug) removeJsonLd("blog-article"); };
  }, [slug, post, postImage]);

  useEffect(() => {
    if (slug) {
      api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
    } else {
      api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
      setPost(null);
    }
  }, [slug]);

  if (slug && post) {
    const shareUrl = `${SITE_URL}/blog/${slug}`;
    return (
      <div className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.24em] link-underline">
            <ArrowLeft size={14} /> All Posts
          </Link>
          <div className="mt-8 eyebrow text-ink/60">{post.tag} · {fmtDate(post.date)}</div>
          <h1 className="mt-3 font-rye text-4xl uppercase leading-[0.95] sm:text-5xl lg:text-6xl">{post.title}</h1>
          {post.image_url && (
            <div className="mt-8 border-2 border-ink shadow-hard" data-testid="blog-detail-cover">
              <img src={resolveUrl(post.image_url)} alt={post.title} className="aspect-[16/9] w-full object-cover" />
            </div>
          )}
          <div className="mt-8 h-px w-24 bg-ink" />
          <article className="prose mt-8 max-w-none whitespace-pre-line font-serif text-base leading-relaxed text-ink/90">
            {post.body}
          </article>
          <SharePost post={post} url={shareUrl} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-ink py-16 md:py-24" data-testid="blog-hero">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="eyebrow mb-3 text-ink/70">Field notes</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">The Blog</h1>
          <p className="mt-6 max-w-2xl font-serif text-base leading-relaxed text-ink/80">
            Recipes, pit techniques, news from the retailers, and dispatches from the road. Written by the family — no
            ghostwriters, no filler.
          </p>
        </div>
      </section>

      <section className="border-b border-ink py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Recent posts" title="From The Pit" />
          <div className="mt-12 grid grid-cols-1 gap-0 border border-ink md:grid-cols-2">
            {posts.map((p, i) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.id}
                data-testid={`blog-post-${i}`}
                className={`group grain-block flex flex-col justify-between p-8 transition-colors duration-200 hover:bg-ink hover:text-bone ${i % 2 === 0 ? "md:border-r border-ink" : ""} ${i < posts.length - (posts.length % 2 || 2) ? "border-b border-ink" : ""}`}
              >
                <div>
                  {p.image_url && (
                    <div className="mb-6 -mx-8 -mt-8 border-b border-ink" data-testid={`blog-post-${i}-cover`}>
                      <img src={resolveUrl(p.image_url)} alt={p.title} className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                    </div>
                  )}
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.24em] opacity-70">
                    <span>{p.tag}</span>
                    <span>{fmtDate(p.date)}</span>
                  </div>
                  <h3 className="mt-4 font-alfa text-3xl uppercase leading-none">{p.title}</h3>
                  <p className="mt-4 font-serif text-sm leading-relaxed opacity-90">{p.excerpt}</p>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] link-underline">
                  Read post →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
